import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Card, CardBody, CardHeader, Button } from "@/Components/ui";
import BreadCrumb from "@/Components/Common/BreadCrumb";
import { toast } from "react-hot-toast";
import { TableOrder, tableOrderService } from "@/api/services/table-order.service";

const statusLabels: Record<string, string> = {
  NEW: "신규",
  ACCEPTED: "접수",
  SERVED: "서빙완료",
  CANCELED: "취소",
};

const fulfillmentTypeLabels: Record<string, string> = {
  INSTANT: "즉시 제공",
  KITCHEN: "제조 필요",
};

const formatTableLabel = (order: TableOrder) => {
  if (order.table_name && order.table_code) {
    return `${order.table_name} (${order.table_code})`;
  }

  if (order.order_type === "pickup") {
    return "픽업 주문";
  }

  return "테이블 미지정";
};

const canCancelOrderFromStatusAction = (order: TableOrder) => {
  return (
    (order.status === "NEW" || order.status === "ACCEPTED") &&
    (order.payment_status === "UNPAID" || order.payment_status === "PAYMENT_FAILED")
  );
};

const ORDER_PAGE_SIZE = 20;

const Orders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<TableOrder[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [orderTypeFilter, setOrderTypeFilter] = useState<string>("");
  const [fulfillmentTypeFilter, setFulfillmentTypeFilter] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = {};
      if (statusFilter) params.status = statusFilter;
      if (orderTypeFilter) params.order_type = orderTypeFilter;
      if (fulfillmentTypeFilter) params.fulfillment_type = fulfillmentTypeFilter;
      params.page = currentPage;
      params.page_size = ORDER_PAGE_SIZE;

      const response = await tableOrderService.listTableOrders(params);
      const data = response.data;
      const nextOrders = data.results || [];
      const nextCount = data.count || 0;

      setOrders(nextOrders);
      setTotalCount(nextCount);

      if (nextOrders.length === 0 && currentPage > 1 && nextCount > 0) {
        const fallbackPage = Math.ceil(nextCount / ORDER_PAGE_SIZE);
        if (fallbackPage < currentPage) {
          setCurrentPage(fallbackPage);
        }
      }
    } catch (error) {
      toast.error("주문 목록을 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, orderTypeFilter, fulfillmentTypeFilter, currentPage]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(totalCount / ORDER_PAGE_SIZE));
  }, [totalCount]);

  const visiblePages = useMemo(() => {
    const pages: number[] = [];
    const maxButtons = 5;
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, start + maxButtons - 1);

    start = Math.max(1, end - maxButtons + 1);
    for (let page = start; page <= end; page += 1) {
      pages.push(page);
    }
    return pages;
  }, [currentPage, totalPages]);

  const handleChangeStatus = async (order: TableOrder, nextStatus: "ACCEPTED" | "SERVED" | "CANCELED") => {
    try {
      await tableOrderService.updateTableOrderStatus(order.id, nextStatus);
      await loadOrders();
      toast.success("주문 상태가 변경되었습니다.");
    } catch (error) {
      toast.error("주문 상태 변경에 실패했습니다.");
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="주문 관리" pageTitle="현장주문" />

        <Card>
          <CardHeader className="d-flex align-items-center justify-content-between">
            <h5 className="mb-0">주문 목록</h5>
            <div className="d-flex gap-2" style={{ minWidth: 360 }}>
              <select
                className="form-select"
                value={statusFilter}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">전체 상태</option>
                <option value="NEW">신규</option>
                <option value="ACCEPTED">접수</option>
                <option value="SERVED">서빙완료</option>
                <option value="CANCELED">취소</option>
              </select>
              <select
                className="form-select"
                value={orderTypeFilter}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  setOrderTypeFilter(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">전체 타입</option>
                <option value="serving">서빙</option>
                <option value="pickup">픽업</option>
              </select>
              <select
                className="form-select"
                value={fulfillmentTypeFilter}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  setFulfillmentTypeFilter(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">전체 제공 타입</option>
                <option value="KITCHEN">제조 필요</option>
                <option value="INSTANT">즉시 제공</option>
              </select>
            </div>
          </CardHeader>
          <CardBody>
            {loading ? (
              <p className="text-muted mb-0">불러오는 중...</p>
            ) : (
              <div className="w-full overflow-x-auto">
                <table className="w-full mb-0 text-sm border-y border-slate-200">
                  <thead className="bg-slate-50 border-b border-slate-200 text-slate-500">
                    <tr>
                      <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap">ID</th>
                      <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap">주문 타입</th>
                      <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap">제공 타입</th>
                      <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap">테이블</th>
                      <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap">생성자</th>
                      <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap">금액</th>
                      <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap">상태</th>
                      <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap">생성시각</th>
                      <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap">관리</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {orders.map((order) => (
                      <tr key={order.id} className="hover:bg-slate-50/80 transition-colors">
                        <td className="p-4 align-middle whitespace-nowrap text-slate-700">{order.id}</td>
                        <td className="p-4 align-middle whitespace-nowrap text-slate-700">{order.order_type === "pickup" ? "픽업" : "서빙"}</td>
                        <td className="p-4 align-middle whitespace-nowrap text-slate-700">{fulfillmentTypeLabels[order.fulfillment_type || ""] || order.fulfillment_type || "-"}</td>
                        <td className="p-4 align-middle whitespace-nowrap text-slate-700">{formatTableLabel(order)}</td>
                        <td className="p-4 align-middle whitespace-nowrap text-slate-700">{order.created_by_phone}</td>
                        <td className="p-4 align-middle whitespace-nowrap text-slate-700">{order.total_amount.toLocaleString()}원</td>
                        <td className="p-4 align-middle whitespace-nowrap text-slate-700">{statusLabels[order.status] || order.status}</td>
                        <td className="p-4 align-middle whitespace-nowrap text-slate-700">{new Date(order.created_at).toLocaleString()}</td>
                        <td className="p-4 align-middle whitespace-nowrap text-slate-700">
                          <Button color="soft-primary" size="sm" className="me-1" onClick={() => navigate(`/table-order/order-detail/${order.id}`)}>상세</Button>
                          {order.status === "NEW" && (
                            <Button color="soft-success" size="sm" className="me-1" onClick={() => handleChangeStatus(order, "ACCEPTED")}>접수</Button>
                          )}
                          {order.status === "ACCEPTED" && (
                            <Button color="soft-info" size="sm" className="me-1" onClick={() => handleChangeStatus(order, "SERVED")}>서빙완료</Button>
                          )}
                          {(order.status === "NEW" || order.status === "ACCEPTED") && (
                            <Button
                              color="soft-danger"
                              size="sm"
                              disabled={!canCancelOrderFromStatusAction(order)}
                              onClick={() => handleChangeStatus(order, "CANCELED")}
                            >
                              취소
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {orders.length === 0 && (
                      <tr>
                        <td colSpan={9} className="text-center text-muted py-8">주문이 없습니다.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {totalCount > 0 && (
              <div className="d-flex align-items-center justify-content-between mt-3">
                <small className="text-muted">
                  총 {totalCount}건 · {currentPage}/{totalPages} 페이지
                </small>
                <div className="d-flex align-items-center gap-1">
                  <Button
                    size="sm"
                    color="soft-secondary"
                    disabled={currentPage <= 1}
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  >
                    이전
                  </Button>
                  {visiblePages.map((page) => (
                    <Button
                      key={page}
                      size="sm"
                      color={page === currentPage ? "primary" : "soft-secondary"}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </Button>
                  ))}
                  <Button
                    size="sm"
                    color="soft-secondary"
                    disabled={currentPage >= totalPages}
                    onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                  >
                    다음
                  </Button>
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default Orders;
